<template>
  <div ref="wrapper" class="wrapper" v-resize="onResize">
    <ECharts ref="chart" class="chart" :options="{}" autoresize />
  </div>
</template>

<script>
// Modified.by.KWShin.2026.10.29. 테마 전환(switchTheme)에 cloneDeep, isEqual, isPlainObject 를 쓴다
// import { merge, isArray } from 'lodash';
import { merge, isArray, cloneDeep, isEqual, isPlainObject } from 'lodash';
import ECharts from 'vue-echarts/components/ECharts.vue';
import 'echarts/lib/chart/bar';
import 'echarts/lib/chart/line';
import 'echarts/lib/chart/map';
import 'echarts/lib/component/tooltip';
import 'echarts/lib/component/legend';
import 'echarts/lib/component/title';
import 'echarts/lib/component/visualMap';
import 'echarts/lib/component/polar';
import 'echarts/map/js/world';
import 'echarts-liquidfill';
import initial from './options/initial.js';
import dark from './options/dark.js';
import light from './options/light.js';

// Added.by.KWShin.2026.10.29. 테마 전환: 이전 테마(from)에서 온 값만 새 테마(to)의 값으로 바꾼다.
// 예전에는 합쳐 둔 옵션 위에 새 테마를 통째로 덮어써(merge) 차트가 직접 정한 값(축 라벨 크기·색 등)이 테마 값으로 바뀌었고,
// 테마를 되돌려도 원래 값으로 돌아오지 않았다(데이터가 다시 들어오는 차트만 잠깐 바뀌었다가 돌아왔다).
// 지금 값이 비었거나 이전 테마의 값과 같으면 테마에서 온 값으로 보고 바꾸고, 다르면 차트가 정한 값이므로 그대로 둔다.
// (배열로 정한 축처럼 모양이 테마와 다른 값도 예전처럼 그대로 둔다)
function switchTheme(target, from, to) {
  Object.keys(to).forEach(key => {
    const next = to[key];
    const own = target[key];
    if (isPlainObject(next) && isPlainObject(own)) switchTheme(own, from?.[key], next);
    else if (isPlainObject(next) && own !== undefined) return;
    else if (own === undefined || isEqual(own, from?.[key])) target[key] = cloneDeep(next);
  });
  return target;
}

export default {
  components: {
    ECharts,
  },
  props: {
    init: {
      type: Object,
      default: () => ({}),
    },
    options: {
      type: Object,
      required: true,
    },
    ready: {
      type: Function,
      default: p => p,
    },
    chart: [Object],
    echart: [Object],
  },
  data() {
    return {
      chartOptions: {},
      onReady: false,
    };
  },
  computed: {
    isDark() {
      return this.$vuetify.theme.isDark;
    },
    theme() {
      return this.isDark ? dark : light;
    },
    // Removed.by.KWShin.2026.10.29. 테마 전환이 축 선 표시를 바꾸지 않으므로(switchTheme) 되돌려 놓을 필요가 없다
    // axisLines() {
    //   return {
    //     x: this.options.xAxis?.axisLine?.show || false,
    //     y: this.options.yAxis?.axisLine?.show || false,
    //   };
    // },
  },
  watch: {
    isDark: {
      handler: 'changeIsDarkHandler',
    },
    options: {
      deep: true,
      handler: 'changeOptionsHandler',
    },
    'chartOptions.series': {
      deep: true,
      handler(series) {
        this.$emit('change:series', series);
      },
    },
  },
  methods: {
    initChart() {
      this.$refs.chart.chart.setOption(merge(this.chartOptions, initial, this.theme, this.init), true);
    },
    onResize() {
      this.$refs.chart.$el.style.height = `${this.$refs.wrapper.offsetHeight}px`;
      this.$refs.chart.$el.style.width = `${this.$refs.wrapper.offsetWidth}px`;
    },
    changeOptionsHandler() {
      this.setInitializeArrayTypeOption();
      this.setSeriesOptions();
      this.chartOptions = merge({}, this.chartOptions, this.options);
      this.$refs.chart.chart.setOption(this.chartOptions, true);
    },
    setInitializeArrayTypeOption() {
      const length = target => (isArray(target) ? target.length : 1);
      // series
      if (!!this.chartOptions.series && !!this.options.series) {
        if (length(this.chartOptions.series) > length(this.options.series)) this.chartOptions.series = [];
        if (isArray(this.chartOptions.series)) {
          this.chartOptions.series.forEach((series, index) => {
            if (isArray(series.data)) {
              if (series.data.length > this.options.series[index].data.length) this.chartOptions.series[index].data = [];
            } else {
              if (series.length > this.options.series.length) this.chartOptions.series = [];
            }
          });
        } else {
          if (length(this.chartOptions.series.data) > length(this.options.series.data)) this.chartOptions.series.data = [];
        }
      }
      // xAxis
      if (this.chartOptions.xAxis?.type == 'category' && !!this.options?.xAxis?.data) {
        if (length(this.chartOptions.xAxis.data) > length(this.options.xAxis.data)) this.chartOptions.xAxis.data = [];
      }
      // yAxis
      if (this.chartOptions.yAxis?.type == 'category' && !!this.options?.yAxis?.data) {
        if (length(this.chartOptions.yAxis.data) > length(this.options.yAxis.data)) this.chartOptions.yAxis.data = [];
      }
    },
    changeIsDarkHandler() {
      // Modified.by.KWShin.2026.10.29. 이전 테마에서 온 값만 바꾼다(switchTheme 참고). 축 선 표시도 처음 그린 그대로 둔다.
      // (예전처럼 옵션에 axisLine.show 가 없다고 축 선을 끄면 처음 화면(initial 의 show: true)과 달라진다)
      // const option = merge(this.chartOptions, this.theme);
      // option.xAxis.axisLine.show = this.axisLines.x;
      // option.yAxis.axisLine.show = this.axisLines.y;
      const option = switchTheme(this.chartOptions, this.isDark ? light : dark, this.theme);
      this.$refs.chart.chart.setOption(option, true);
    },
    setSeriesOptions() {
      const showSymbol = series => {
        if (series.type == 'line' && !Object.prototype.hasOwnProperty.call(series, 'showSymbol')) series.showSymbol = false;
      };
      if (this.options.series) (isArray(this.options.series) ? this.options.series : [this.options.series]).forEach(series => showSymbol(series));
    },
    // add event chart
    addEventChart() {
      const canvas = this.$refs.chart.chart.getDom().getElementsByTagName('canvas')[0];
      if (canvas) {
        canvas.addEventListener('click', event => this.$emit('click', event, this.xAxisValue(event)));
        canvas.addEventListener('contextmenu', event => this.$emit('contextmenu', event, this.xAxisValue(event)));
      }
    },
    xAxisValue(event) {
      let xAxisValue = null;
      const data = this.$refs.chart.chart.getOption().xAxis[0].data;
      if (data) {
        const pixels = data.map((value, index) => this.$refs.chart.chart.convertToPixel({ xAxisIndex: 0 }, index));
        if (pixels.length === 0) return xAxisValue;
        const closestValue = pixels.reduce(function(prev, curr) {
          return Math.abs(curr - event.offsetX) < Math.abs(prev - event.offsetX) ? curr : prev;
        });
        const index = pixels.findIndex(value => Math.abs(value) === closestValue);
        xAxisValue = data[index];
      }
      return xAxisValue;
    },
  },
  created() {
    this.$nextTick(() => {
      this.initChart();
      this.$refs.chart.chart.on('finished', () => {
        if (!this.onReady) {
          this.$emit('update:chart', this.$refs.chart);
          this.$emit('update:echart', this.$refs.chart.chart);
        }
        if (this.onReady) {
          const caller = this.$parent;
          this.ready.apply(this, [this.$refs.chart.chart, caller]);
          this.$refs.chart.chart.off('finished');
          this.addEventChart();
        }
        this.onReady = true;
      });
    });
  },
};
</script>

<style scoped>
.wrapper {
  position: relative;
  height: 100%;
  width: 100%;
}
.chart {
  position: absolute;
  top: 0px;
  left: 0px;
  z-index: 0;
}
</style>
